<template>
    <Transition name="modal-fade">
        <div v-if="isVisible" class="cf-overlay" role="dialog" aria-modal="true" @click.self="handleOverlayClick">
            <Transition name="modal-slide">
                <div v-if="isVisible" class="cf-dialog cf-dialog--xxl" @click.stop>

                    <!-- ========== HEADER ========== -->
                    <div class="cf-modal-head cf-dialog-head">
                        <div class="cf-modal-head__main">
                            <span class="cf-modal-head__icon"><i class="ri-file-list-3-line"></i></span>
                            <div>
                                <h4 class="cf-dialog-title">{{ titleModal }}</h4>
                                <p class="cf-modal-head__sub">Documents commerciaux — achats &amp; ventes</p>
                            </div>
                        </div>
                        <div class="cf-modal-head__aside">
                            <div class="ti-btn-group" style="position:relative;display:inline-block;">
                                <button class="ds-btn-ghost !py-1 !px-2 !text-[0.75rem]" type="button"
                                        :aria-expanded="openMenu === 'opts'" @click.stop="toggleMenu('opts')">
                                    <i class="ri-more-2-fill"></i>
                                </button>
                                <ul v-show="openMenu === 'opts'" class="ag-menu">
                                    <li><button @click="handleCustomizeLabels();openMenu=null" class="ag-menu-item">
                                        <i class="ri-price-tag-3-line cf-menu-icon"></i> Personnaliser les libellés</button></li>
                                    <li><button @click="exportData();openMenu=null" class="ag-menu-item">
                                        <i class="ri-download-2-line cf-menu-icon"></i> Exporter</button></li>
                                    <li><button @click="refreshData();openMenu=null" class="ag-menu-item">
                                        <i class="ri-refresh-line cf-menu-icon"></i> Actualiser</button></li>
                                </ul>
                            </div>
                            <button @click="close" type="button" class="cf-dialog-close" aria-label="Fermer"><i class="ri-close-line"></i></button>
                        </div>
                    </div>

                    <!-- ========== TOOLBAR ========== -->
                    <div class="cf-dialog-toolbar">
                        <div class="cf-toolbar-row">
                            <div class="search-wrap">
                                <i class="ri-search-line search-icon"></i>
                                <input v-model="searchQuery" type="search" class="search-input"
                                       placeholder="Rechercher par n° document, client, fournisseur..." @input="handleSearchDebounced">
                                <button v-if="searchQuery" @click="clearSearch" class="search-clear" aria-label="Effacer"><i class="ri-close-line"></i></button>
                            </div>
                            <button v-if="canCreate" @click="handleAdd" class="ti-btn ti-btn-primary-full">
                                <i class="ri-add-line align-middle"></i>
                                <span class="hidden sm:inline">&nbsp;Nouveau document</span>
                                <span class="sm:hidden">&nbsp;Ajouter</span>
                            </button>
                        </div>
                        <div class="cf-toolbar-meta">
                            <div class="cf-period">
                                <button v-for="period in periodOptions" :key="period.value"
                                        @click="handlePeriodChange(period.value)"
                                        class="cf-period-btn" :class="{ 'cf-period-btn--active': selectedPeriod === period.value }">
                                    {{ period.label }}
                                </button>
                            </div>
                            <div class="cf-perpage">
                                <label>Afficher</label>
                                <div class="cf-select-wrap">
                                    <select v-model="perPage" class="cf-select--sm">
                                        <option v-for="option in perPageOptions" :key="option" :value="option">{{ option }}</option>
                                    </select>
                                    <i class="ri-arrow-down-s-line cf-select-arrow"></i>
                                </div>
                                <span class="cf-count"><b>{{ filteredData.length }}</b> résultat{{ filteredData.length > 1 ? 's' : '' }}</span>
                                <span v-if="isLoading" class="cf-count"><i class="ri-loader-4-line cf-spin"></i> Chargement…</span>
                            </div>
                        </div>
                    </div>

                    <!-- ========== BODY ========== -->
                    <div class="cf-dialog-body cf-dialog-body--flush">
                        <!-- Desktop table -->
                        <div class="table-scroll-wrap doc-scroll hidden lg:block">
                            <table class="doc-table">
                                <thead>
                                    <tr>
                                        <th v-for="column in tableColumns" :key="column.key" class="th"
                                            :class="{ 'th--sort': column.sortable }"
                                            @click="column.sortable && handleSort(column.key)">
                                            <span class="th-inner">
                                                {{ column.label }}
                                                <i v-if="column.sortable" :class="sortBy === column.key ? (sortDesc ? 'ri-arrow-down-line' : 'ri-arrow-up-line') : 'ri-arrow-up-down-line th-sort-icon'"></i>
                                            </span>
                                        </th>
                                        <th class="th th--center" style="width:4.5rem">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <template v-if="paginatedData.length">
                                        <tr v-for="(item, index) in paginatedData" :key="item.pid" class="table-row">
                                            <td class="td row-number">{{ (currentPage - 1) * perPage + index + 1 }}</td>
                                            <td class="td"><span class="cf-docnum">{{ item.numdoc }}</span></td>
                                            <td class="td">
                                                <div class="doc-client">
                                                    <span class="doc-avatar">{{ getInitials(item.nameclient) }}</span>
                                                    <span class="cf-strong doc-ellip" :title="item.nameclient">{{ item.nameclient }}</span>
                                                </div>
                                            </td>
                                            <td class="td">{{ item.magname }}</td>
                                            <td class="td"><span class="cf-ellip" :title="item.mobservations">{{ item.mobservations || '-' }}</span></td>
                                            <td class="td">{{ item.numbon || '-' }}</td>
                                            <td class="td">{{ formatDate(item.creerler) }}</td>
                                            <td class="td">{{ item.saisiepar }}</td>
                                            <td class="td td--center actions-td" style="overflow:visible;" @click.stop>
                                                <div class="ti-btn-group" style="position:relative;display:inline-block;">
                                                    <button class="ds-btn-ghost !py-1 !px-2 !text-[0.75rem]" type="button"
                                                            :aria-expanded="openMenu === 'row_' + index"
                                                            @click.stop="toggleMenu('row_' + index)">
                                                        <i class="ri-more-2-fill"></i>
                                                    </button>
                                                    <ul v-show="openMenu === 'row_' + index" class="ag-menu">
                                                        <li><button @click="handleOpen(item);openMenu=null" class="ag-menu-item">
                                                            <i class="ri-eye-line cf-menu-icon"></i> Ouvrir</button></li>
                                                        <li v-if="item.nuse === 0 && canUpdate"><button @click="handleEdit(item);openMenu=null" class="ag-menu-item">
                                                            <i class="ri-edit-line cf-menu-icon"></i> Modifier</button></li>
                                                        <li v-if="item.nbp > 0"><button @click="handlePrint(item);openMenu=null" class="ag-menu-item">
                                                            <i class="ri-printer-line cf-menu-icon"></i> Imprimer</button></li>
                                                        <li v-if="item.nbp > 0"><button @click="handleTransform(item);openMenu=null" class="ag-menu-item">
                                                            <i class="ri-swap-line cf-menu-icon"></i> Transformer</button></li>
                                                        <li v-if="canNormalize(item)"><button @click="handleNormalize(item);openMenu=null" class="ag-menu-item">
                                                            <i class="ri-checkbox-circle-line cf-menu-icon"></i> Normaliser</button></li>
                                                        <li v-if="item.nuse === 0 && item.nbp === 0 && canDelete"><button @click="handleDelete(item);openMenu=null" class="ag-menu-item ag-menu-item--danger">
                                                            <i class="ri-delete-bin-line cf-menu-icon"></i> Supprimer</button></li>
                                                    </ul>
                                                </div>
                                            </td>
                                        </tr>
                                    </template>
                                    <tr v-else>
                                        <td colspan="9" class="td">
                                            <div class="empty-state">
                                                <div class="empty-state__icon"><i class="ri-file-list-3-line"></i></div>
                                                <p class="empty-state__title">Aucun document trouvé</p>
                                                <p class="empty-state__sub">{{ searchQuery ? 'Essayez de modifier vos critères de recherche' : 'Commencez par créer un nouveau document' }}</p>
                                                <button v-if="canCreate && !searchQuery" @click="handleAdd" class="ti-btn ti-btn-primary-full" style="margin-top:14px;">
                                                    <i class="ri-add-line align-middle"></i> Créer un document
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Mobile cards -->
                        <div class="lg:hidden doc-scroll cf-cards">
                            <template v-if="paginatedData.length">
                                <div v-for="(item, index) in paginatedData" :key="item.pid" class="cf-card">
                                    <div class="cf-card-head">
                                        <div class="cf-card-headmain">
                                            <span class="cf-card-index">{{ (currentPage - 1) * perPage + index + 1 }}</span>
                                            <span class="cf-docnum">{{ item.numdoc }}</span>
                                        </div>
                                        <span class="cf-badge" :class="item.nuse === 0 ? 'cf-badge--ok' : 'cf-badge--muted'">{{ item.nuse === 0 ? 'Modifiable' : 'Utilisé' }}</span>
                                    </div>
                                    <div class="cf-card-body">
                                        <div class="doc-client" style="margin-bottom:0.6rem;">
                                            <span class="doc-avatar">{{ getInitials(item.nameclient) }}</span>
                                            <div style="min-width:0;">
                                                <p class="cf-card-val doc-ellip" :title="item.nameclient">{{ item.nameclient }}</p>
                                                <p class="cf-card-lbl">{{ item.magname }}</p>
                                            </div>
                                        </div>
                                        <div class="cf-card-grid">
                                            <div><p class="cf-card-lbl">N° Bon</p><p class="cf-card-val">{{ item.numbon || '-' }}</p></div>
                                            <div><p class="cf-card-lbl">Date</p><p class="cf-card-val">{{ formatDate(item.creerler) }}</p></div>
                                        </div>
                                        <div v-if="item.mobservations" class="cf-card-obs">
                                            <p class="cf-card-lbl">Observation</p>
                                            <p class="cf-card-val cf-card-val--muted">{{ item.mobservations }}</p>
                                        </div>
                                    </div>
                                    <div class="cf-card-foot">
                                        <span class="cf-card-by">Par {{ item.saisiepar }}</span>
                                        <div class="cf-actions">
                                            <button @click="handleOpen(item)" class="cf-iconbtn cf-iconbtn--primary" title="Ouvrir"><i class="ri-eye-line"></i></button>
                                            <button @click="openMobileActions(item)" class="cf-iconbtn" title="Plus d'actions"><i class="ri-more-2-fill"></i></button>
                                        </div>
                                    </div>
                                </div>
                            </template>
                            <div v-else class="empty-state">
                                <div class="empty-state__icon"><i class="ri-file-list-3-line"></i></div>
                                <p class="empty-state__title">Aucun document trouvé</p>
                            </div>
                        </div>
                    </div>

                    <!-- ========== FOOTER ========== -->
                    <div class="cf-dialog-foot cf-dialog-foot--split">
                        <p class="cf-foot-info">{{ paginationStart }}–{{ paginationEnd }} sur {{ filteredData.length }} résultats</p>
                        <div class="pagination" v-if="totalPages > 1">
                            <button class="page-btn page-btn--nav" @click="currentPage--" :disabled="currentPage === 1"><i class="ri-arrow-left-s-line"></i></button>
                            <template v-for="pageNum in displayedPages" :key="pageNum">
                                <span v-if="pageNum === '...'" class="cf-ellipsis">…</span>
                                <button v-else class="page-btn" :class="{ 'page-btn--active': currentPage === pageNum }" @click="currentPage = pageNum">{{ pageNum }}</button>
                            </template>
                            <button class="page-btn page-btn--nav" @click="currentPage++" :disabled="currentPage === totalPages || totalPages === 0"><i class="ri-arrow-right-s-line"></i></button>
                        </div>
                    </div>

                </div>
            </Transition>

        <!-- Mobile Action Sheet -->
        <transition name="slide-up">
            <div v-if="showMobileActionSheet && selectedItem" class="fixed inset-0 z-[60]">
                <div class="absolute inset-0 bg-black/50" @click="closeMobileActions"></div>
                <div class="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-800 rounded-t-2xl safe-area-pb">
                    <div class="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full mx-auto mt-3"></div>
                    <div class="p-4 border-b border-gray-200 dark:border-gray-700">
                        <p class="text-sm text-gray-500 dark:text-gray-400">Document</p>
                        <p class="text-lg font-semibold text-gray-900 dark:text-white">{{ selectedItem.numdoc }}</p>
                    </div>
                    <div class="py-2">
                        <button @click="handleOpen(selectedItem); closeMobileActions()" class="w-full px-6 py-4 text-left flex items-center gap-4 hover:bg-gray-100 dark:hover:bg-gray-700">
                            <svg class="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                            <span class="text-gray-900 dark:text-white font-medium">Ouvrir</span>
                        </button>
                        <button v-if="selectedItem.nbp > 0" @click="handlePrint(selectedItem); closeMobileActions()" class="w-full px-6 py-4 text-left flex items-center gap-4 hover:bg-gray-100 dark:hover:bg-gray-700">
                            <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"/></svg>
                            <span class="text-gray-900 dark:text-white font-medium">Imprimer</span>
                        </button>
                        <button v-if="selectedItem.nbp > 0" @click="handleTransform(selectedItem); closeMobileActions()" class="w-full px-6 py-4 text-left flex items-center gap-4 hover:bg-gray-100 dark:hover:bg-gray-700">
                            <svg class="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/></svg>
                            <span class="text-gray-900 dark:text-white font-medium">Transformer</span>
                        </button>
                        <button v-if="selectedItem.nuse === 0 && canUpdate" @click="handleEdit(selectedItem); closeMobileActions()" class="w-full px-6 py-4 text-left flex items-center gap-4 hover:bg-gray-100 dark:hover:bg-gray-700">
                            <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                            <span class="text-gray-900 dark:text-white font-medium">Modifier</span>
                        </button>
                        <button v-if="selectedItem.nuse === 0 && selectedItem.nbp === 0 && canDelete" @click="handleDelete(selectedItem); closeMobileActions()" class="w-full px-6 py-4 text-left flex items-center gap-4 hover:bg-gray-100 dark:hover:bg-gray-700">
                            <svg class="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                            <span class="text-red-600 font-medium">Supprimer</span>
                        </button>

                        <button v-if="canNormalize(selectedItem)" @click="handleNormalize(selectedItem); closeMobileActions()"    class="w-full px-6 py-4 text-left flex items-center gap-4 hover:bg-gray-100 dark:hover:bg-gray-700">
                            <svg class="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"> <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                            <span class="text-red-600 font-medium">Normaliser</span>
                        </button>



                    </div>
                    <div class="p-4 border-t border-gray-200 dark:border-gray-700">
                        <button @click="closeMobileActions" class="w-full py-3 text-center text-gray-600 dark:text-gray-400 font-medium hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl">Annuler</button>
                    </div>
                </div>
            </div>
        </transition>

        <!-- Child Components Slots -->
        <modaltransformation ref="modaltransformation" @elementSelectionnetransformation="handleTransformationSelected" />
        <modalopenform ref="modalopenform" />
        <modalform ref="modalform" @closed="loadData" />
        <modalimpressiondoc ref="modalimpressiondoc" />
        <modalformsuppression ref="modalformsuppression" @closed="loadData" />
        <modalcustomlib ref="modalcustomlib" @elementSelectionnecustomlib="handleCustomLibSelected" />
        <normalisationdelafacture ref="normalisationdelafacture" @closed="loadData" />







        <achat_bcd_etat0 ref="achat_bcd_etat0"  ></achat_bcd_etat0>
        <achat_bcd_etat1 ref="achat_bcd_etat1"  ></achat_bcd_etat1>
        <achat_bcd_etat2 ref="achat_bcd_etat2"  ></achat_bcd_etat2>
        <achat_bcd_etat3 ref="achat_bcd_etat3"  ></achat_bcd_etat3>
        <achat_bcd_etat4 ref="achat_bcd_etat4"  ></achat_bcd_etat4>
        <achat_bcd_etat5 ref="achat_bcd_etat5"  ></achat_bcd_etat5>

        <achat_bondereception_etat0 ref="achat_bondereception_etat0"  ></achat_bondereception_etat0>
        <achat_demandedachat_etat0 ref="achat_demandedachat_etat0"  ></achat_demandedachat_etat0>
        <achat_facture_etat0 ref="achat_facture_etat0"  ></achat_facture_etat0>
        <achat_retour_etat0 ref="achat_retour_etat0"  ></achat_retour_etat0>

        <vente_bcd_etat0 ref="vente_bcd_etat0"  ></vente_bcd_etat0>
        <vente_bl_etat0 ref="vente_bl_etat0"  ></vente_bl_etat0>
        <vente_bpl_etat0 ref="vente_bpl_etat0"  ></vente_bpl_etat0>
        <vente_facture_etat0 ref="vente_facture_etat0"  ></vente_facture_etat0>
        <vente_retour_etat0 ref="vente_retour_etat0"  ></vente_retour_etat0>


        <achat_bondereception_etat1 ref="achat_bondereception_etat1"  ></achat_bondereception_etat1>
        <achat_bondereception_etat2 ref="achat_bondereception_etat2"  ></achat_bondereception_etat2>
        <achat_bondereception_etat3 ref="achat_bondereception_etat3"  ></achat_bondereception_etat3>
        <achat_bondereception_etat4 ref="achat_bondereception_etat4"  ></achat_bondereception_etat4>
        <achat_bondereception_etat5 ref="achat_bondereception_etat5"  ></achat_bondereception_etat5>
        <achat_demandedachat_etat1 ref="achat_demandedachat_etat1"  ></achat_demandedachat_etat1>
        <achat_demandedachat_etat2 ref="achat_demandedachat_etat2"  ></achat_demandedachat_etat2>
        <achat_demandedachat_etat3 ref="achat_demandedachat_etat3"  ></achat_demandedachat_etat3>
        <achat_demandedachat_etat4 ref="achat_demandedachat_etat4"  ></achat_demandedachat_etat4>
        <achat_demandedachat_etat5 ref="achat_demandedachat_etat5"  ></achat_demandedachat_etat5>
        <achat_facture_etat1 ref="achat_facture_etat1"  ></achat_facture_etat1>
        <achat_facture_etat2 ref="achat_facture_etat2"  ></achat_facture_etat2>
        <achat_facture_etat3 ref="achat_facture_etat3"  ></achat_facture_etat3>
        <achat_facture_etat4 ref="achat_facture_etat4"  ></achat_facture_etat4>
        <achat_facture_etat5 ref="achat_facture_etat5"  ></achat_facture_etat5>
        <achat_retour_etat1 ref="achat_retour_etat1"  ></achat_retour_etat1>
        <achat_retour_etat2 ref="achat_retour_etat2"  ></achat_retour_etat2>
        <achat_retour_etat3 ref="achat_retour_etat3"  ></achat_retour_etat3>
        <achat_retour_etat4 ref="achat_retour_etat4"  ></achat_retour_etat4>
        <achat_retour_etat5 ref="achat_retour_etat5"  ></achat_retour_etat5>
        <vente_bcd_etat1 ref="vente_bcd_etat1"  ></vente_bcd_etat1>
        <vente_bcd_etat2 ref="vente_bcd_etat2"  ></vente_bcd_etat2>
        <vente_bcd_etat3 ref="vente_bcd_etat3"  ></vente_bcd_etat3>
        <vente_bcd_etat4 ref="vente_bcd_etat4"  ></vente_bcd_etat4>
        <vente_bcd_etat5 ref="vente_bcd_etat5"  ></vente_bcd_etat5>
        <vente_bl_etat1 ref="vente_bl_etat1"  ></vente_bl_etat1>
        <vente_bl_etat2 ref="vente_bl_etat2"  ></vente_bl_etat2>
        <vente_bl_etat3 ref="vente_bl_etat3"  ></vente_bl_etat3>
        <vente_bl_etat4 ref="vente_bl_etat4"  ></vente_bl_etat4>
        <vente_bl_etat5 ref="vente_bl_etat5"  ></vente_bl_etat5>
        <vente_bpl_etat1 ref="vente_bpl_etat1"  ></vente_bpl_etat1>

        <vente_bpl_etat2 ref="vente_bpl_etat2"  ></vente_bpl_etat2>
        <vente_bpl_etat3 ref="vente_bpl_etat3"  ></vente_bpl_etat3>
        <vente_bpl_etat4 ref="vente_bpl_etat4"  ></vente_bpl_etat4>
        <vente_bpl_etat5 ref="vente_bpl_etat5"  ></vente_bpl_etat5>
        <vente_devis_etat0 ref="vente_devis_etat0"  ></vente_devis_etat0>
        <vente_devis_etat1 ref="vente_devis_etat1"  ></vente_devis_etat1>
        <vente_devis_etat2 ref="vente_devis_etat2"  ></vente_devis_etat2>
        <vente_devis_etat3 ref="vente_devis_etat3"  ></vente_devis_etat3>
        <vente_devis_etat4 ref="vente_devis_etat4"  ></vente_devis_etat4>
        <vente_devis_etat5 ref="vente_devis_etat5"  ></vente_devis_etat5>
        <vente_facture_etat0 ref="vente_facture_etat0"  ></vente_facture_etat0>
        <vente_facture_etat1 ref="vente_facture_etat1"  ></vente_facture_etat1>
        <vente_facture_etat2 ref="vente_facture_etat2"  ></vente_facture_etat2>
        <vente_facture_etat3 ref="vente_facture_etat3"  ></vente_facture_etat3>
        <vente_facture_etat4 ref="vente_facture_etat4"  ></vente_facture_etat4>
        <vente_facture_etat5 ref="vente_facture_etat5"  ></vente_facture_etat5>
        <vente_retour_etat1 ref="vente_retour_etat1"  ></vente_retour_etat1>
        <vente_retour_etat2 ref="vente_retour_etat2"  ></vente_retour_etat2>
        <vente_retour_etat3 ref="vente_retour_etat3"  ></vente_retour_etat3>
        <vente_retour_etat4 ref="vente_retour_etat4"  ></vente_retour_etat4>
        <vente_retour_etat5 ref="vente_retour_etat5"  ></vente_retour_etat5>





    </div>
    </Transition>
</template>

<script>
    import achat_bcd_etat0 from "@/components/views/pages/gestioncommerciale/achatvente/etat/achat_bcd/0";

    const axios =require('axios');
    import { isset, randomString } from '@/allfunction';
    import * as JwtService from '@/common/jwt.service';

    // Modal Components
    import modalcustomlib from '@/components/views/pages/gestioncommerciale/modal_customlib';
    import modalopenform from '@/components/views/pages/gestioncommerciale/achatvente/modal_openform';
    import normalisationdelafacture from '@/components/views/pages/gestioncommerciale/achatvente/normalisationdelafacture';
    import modalform from '@/components/views/pages/gestioncommerciale/achatvente/default/form';
    import modalformsuppression from '@/components/views/pages/gestioncommerciale/achatvente/default/formsuppression';
    import modalimpressiondoc from '@/components/views/pages/stock/mvtstock/modal_etatdocstock';
    import modaltransformation from '@/components/views/pages/gestioncommerciale/modal_transformation';
    import achat_bcd_etat1 from "@/components/views/pages/gestioncommerciale/achatvente/etat/achat_bcd/1";
    import achat_bcd_etat2 from "@/components/views/pages/gestioncommerciale/achatvente/etat/achat_bcd/2";
    import achat_bcd_etat3 from "@/components/views/pages/gestioncommerciale/achatvente/etat/achat_bcd/3";
    import achat_bcd_etat4 from "@/components/views/pages/gestioncommerciale/achatvente/etat/achat_bcd/4";
    import achat_bcd_etat5 from "@/components/views/pages/gestioncommerciale/achatvente/etat/achat_bcd/5";
    import achat_bondereception_etat0
        from "@/components/views/pages/gestioncommerciale/achatvente/etat/achat_bondereception/0";
    import achat_demandedachat_etat0
        from "@/components/views/pages/gestioncommerciale/achatvente/etat/achat_demandedachat/0";
    import achat_facture_etat0 from "@/components/views/pages/gestioncommerciale/achatvente/etat/achat_facture/0";
    import achat_retour_etat0 from "@/components/views/pages/gestioncommerciale/achatvente/etat/achat_retour/0";
    import vente_bcd_etat0 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_bcd/0";
    import vente_bpl_etat0 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_bpl/0";
    import vente_bl_etat0 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_bl/0";
    import vente_retour_etat0 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_retour/0";
    import achat_bondereception_etat1
        from "@/components/views/pages/gestioncommerciale/achatvente/etat/achat_bondereception/1";
    import achat_bondereception_etat2
        from "@/components/views/pages/gestioncommerciale/achatvente/etat/achat_bondereception/2";
    import achat_bondereception_etat3
        from "@/components/views/pages/gestioncommerciale/achatvente/etat/achat_bondereception/3";
    import achat_bondereception_etat4
        from "@/components/views/pages/gestioncommerciale/achatvente/etat/achat_bondereception/4";
    import achat_bondereception_etat5
        from "@/components/views/pages/gestioncommerciale/achatvente/etat/achat_bondereception/5";
    import achat_demandedachat_etat1
        from "@/components/views/pages/gestioncommerciale/achatvente/etat/achat_demandedachat/1";
    import achat_demandedachat_etat2
        from "@/components/views/pages/gestioncommerciale/achatvente/etat/achat_demandedachat/2";
    import achat_demandedachat_etat3
        from "@/components/views/pages/gestioncommerciale/achatvente/etat/achat_demandedachat/3";
    import achat_demandedachat_etat4
        from "@/components/views/pages/gestioncommerciale/achatvente/etat/achat_demandedachat/4";
    import achat_demandedachat_etat5
        from "@/components/views/pages/gestioncommerciale/achatvente/etat/achat_demandedachat/5";
    import achat_facture_etat1 from "@/components/views/pages/gestioncommerciale/achatvente/etat/achat_facture/1";
    import achat_facture_etat2 from "@/components/views/pages/gestioncommerciale/achatvente/etat/achat_facture/2";
    import achat_facture_etat3 from "@/components/views/pages/gestioncommerciale/achatvente/etat/achat_facture/3";
    import achat_facture_etat4 from "@/components/views/pages/gestioncommerciale/achatvente/etat/achat_facture/4";
    import achat_facture_etat5 from "@/components/views/pages/gestioncommerciale/achatvente/etat/achat_facture/5";
    import achat_retour_etat1 from "@/components/views/pages/gestioncommerciale/achatvente/etat/achat_retour/1";
    import achat_retour_etat2 from "@/components/views/pages/gestioncommerciale/achatvente/etat/achat_retour/2";
    import achat_retour_etat3 from "@/components/views/pages/gestioncommerciale/achatvente/etat/achat_retour/3";
    import achat_retour_etat4 from "@/components/views/pages/gestioncommerciale/achatvente/etat/achat_retour/4";
    import achat_retour_etat5 from "@/components/views/pages/gestioncommerciale/achatvente/etat/achat_retour/5";
    import vente_bcd_etat1 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_bcd/1";
    import vente_bcd_etat2 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_bcd/2";
    import vente_bcd_etat3 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_bcd/3";
    import vente_bcd_etat4 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_bcd/4";
    import vente_bcd_etat5 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_bcd/5";
    import vente_bl_etat1 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_bl/1";
    import vente_bl_etat2 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_bl/2";
    import vente_bl_etat3 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_bl/3";
    import vente_bl_etat4 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_bl/4";
    import vente_bl_etat5 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_bl/5";
    import vente_bpl_etat1 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_bpl/1";
    import vente_bpl_etat2 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_bpl/2";
    import vente_bpl_etat3 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_bpl/3";
    import vente_bpl_etat4 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_bpl/4";
    import vente_bpl_etat5 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_bpl/5";
    import vente_devis_etat0 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_devis/0";
    import vente_devis_etat1 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_devis/1";
    import vente_devis_etat2 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_devis/2";
    import vente_devis_etat3 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_devis/3";
    import vente_devis_etat4 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_devis/4";
    import vente_devis_etat5 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_devis/5";
    import vente_facture_etat0 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_facture/0";
    import vente_facture_etat1 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_facture/1";
    import vente_facture_etat2 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_facture/2";
    import vente_facture_etat3 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_facture/3";
    import vente_facture_etat4 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_facture/4";
    import vente_facture_etat5 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_facture/5";
    import vente_retour_etat1 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_retour/1";
    import vente_retour_etat2 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_retour/2";
    import vente_retour_etat3 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_retour/3";
    import vente_retour_etat4 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_retour/4";
    import vente_retour_etat5 from "@/components/views/pages/gestioncommerciale/achatvente/etat/vente_retour/5";
    import qrcodetoimage from "qrcode";
    import permissionHelper from "@/service/directives/permissionHelper";
    import {
        achatscommandeadd,
        achatscommandedelete,
        achatscommandeedit,
        achatsconsolidationfactureadd,
        achatsconsolidationfacturedelete,
        achatsconsolidationfactureedit,
        achatsdemandeadd,
        achatsdemandedelete,
        achatsdemandeedit,
        achatsfactureadd,
        achatsfacturedelete,
        achatsfactureedit,
        achatsreceptionadd,
        achatsreceptiondelete,
        achatsreceptionedit,
        achatsretouradd,
        achatsretourdelete,
        achatsretouredit,
        ventesavoiradd,
        ventesavoirdelete,
        ventesavoiredit,
        ventescommandeadd,
        ventescommandedelete,
        ventescommandeedit,
        ventesconsolidationfactureventeadd,
        ventesconsolidationfactureventedelete,
        ventesconsolidationfactureventeedit,
        ventesdevisadd,
        ventesdevisdelete,
        ventesdevisedit,
        ventesfactureadd,
        ventesfacturedelete,
        ventesfactureedit,
        venteslivraisonadd,
        venteslivraisondelete,
        venteslivraisonedit,
        ventespreparationadd,
        ventespreparationdelete,
        ventespreparationedit
    } from "@/service/directives/vpermission";

    // Debounce utility
    function debounce(fn, delay) {
        let timeoutId;
        return function(...args) {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => fn.apply(this, args), delay);
        };
    }

    export default {
        /* eslint-disable */
        name: 'DocumentManager',

        components: {
            modalform,
            modalformsuppression,
            modaltransformation,
            modalcustomlib,
            normalisationdelafacture,
            modalimpressiondoc,
            modalopenform,


            achat_bcd_etat0,
            achat_bcd_etat1,
            achat_bcd_etat2,
            achat_bcd_etat3,
            achat_bcd_etat4,
            achat_bcd_etat5,

            achat_bondereception_etat0,
            achat_demandedachat_etat0,
            achat_facture_etat0,
            achat_retour_etat0,
            vente_bcd_etat0,
            vente_bpl_etat0,
            vente_bl_etat0,
            vente_retour_etat0,

            achat_bondereception_etat1,
            achat_bondereception_etat2,
            achat_bondereception_etat3,
            achat_bondereception_etat4,
            achat_bondereception_etat5,
            achat_demandedachat_etat1,
            achat_demandedachat_etat2,
            achat_demandedachat_etat3,
            achat_demandedachat_etat4,
            achat_demandedachat_etat5,
            achat_facture_etat1,
            achat_facture_etat2,
            achat_facture_etat3,
            achat_facture_etat4,
            achat_facture_etat5,
            achat_retour_etat1,
            achat_retour_etat2,
            achat_retour_etat3,
            achat_retour_etat4,
            achat_retour_etat5,
            vente_bcd_etat1,
            vente_bcd_etat2,
            vente_bcd_etat3,
            vente_bcd_etat4,
            vente_bcd_etat5,
            vente_bl_etat1,
            vente_bl_etat2,
            vente_bl_etat3,
            vente_bl_etat4,
            vente_bl_etat5,
            vente_bpl_etat1,
            vente_bpl_etat2,
            vente_bpl_etat3,
            vente_bpl_etat4,
            vente_bpl_etat5,
            vente_devis_etat0,
            vente_devis_etat1,
            vente_devis_etat2,
            vente_devis_etat3,
            vente_devis_etat4,
            vente_devis_etat5,
            vente_facture_etat0,
            vente_facture_etat1,
            vente_facture_etat2,
            vente_facture_etat3,
            vente_facture_etat4,
            vente_facture_etat5,
            vente_retour_etat1,
            vente_retour_etat2,
            vente_retour_etat3,
            vente_retour_etat4,
            vente_retour_etat5,


        },

        data() {
            return {
                isVisible: false,
                isLoading: false,
                showMobileMenu: false,
                showDesktopMenu: false,
                showMobileActionSheet: false,
                titleModal: '',
                documentType: '',
                documentCategory: '',
                dataTable: [],
                otherData: [],
                inputLabelForms: {},
                customLabelForms: {},
                searchQuery: '',
                selectedPeriod: '1d',
                sortBy: 'creerler',
                sortDesc: true,
                currentPage: 1,
                perPage: 10,
                perPageOptions: [5, 10, 25, 50, 100],
                periodOptions: [
                    { label: '1J', value: '1d' },
                    { label: '3J', value: '3d' },
                    { label: '1S', value: '7d' },
                    { label: '1M', value: '1M' },
                    { label: '3M', value: '3M' },
                    { label: '6M', value: '6M' },
                    { label: '1A', value: '12M' },
                ],
                tableColumns: [
                    { key: 'index', label: 'N°', sortable: false, width: 'w-16' },
                    { key: 'numdoc', label: 'N° Document', sortable: true },
                    { key: 'nameclient', label: 'Client / Fournisseur', sortable: true },
                    { key: 'magname', label: 'Magasin', sortable: true },
                    { key: 'mobservations', label: 'Observation', sortable: false },
                    { key: 'numbon', label: 'N° Bon', sortable: true },
                    { key: 'creerler', label: 'Créé le', sortable: true },
                    { key: 'saisiepar', label: 'Créé par', sortable: true },
                ],
                canCreate: false,
                canUpdate: false,
                canDelete: false,
                selectedItem: null,
                openMenu: null,
            };
        },

        computed: {
            filteredData() {
                let data = [...this.dataTable];
                if (this.searchQuery) {
                    const query = this.searchQuery.toLowerCase().trim();
                    data = data.filter(item =>
                        item.numdoc?.toString().toLowerCase().includes(query) ||
                        item.magname?.toString().toLowerCase().includes(query) ||
                        item.nameclient?.toString().toLowerCase().includes(query) ||
                        item.numbon?.toString().toLowerCase().includes(query)
                    );
                }
                if (this.sortBy) {
                    data.sort((a, b) => {
                        let valA = a[this.sortBy];
                        let valB = b[this.sortBy];
                        if (typeof valA === 'string') {
                            valA = valA.toLowerCase();
                            valB = valB?.toLowerCase() || '';
                        }
                        if (valA < valB) return this.sortDesc ? 1 : -1;
                        if (valA > valB) return this.sortDesc ? -1 : 1;
                        return 0;
                    });
                }
                return data;
            },
            paginatedData() {
                const start = (this.currentPage - 1) * this.perPage;
                return this.filteredData.slice(start, start + this.perPage);
            },
            totalPages() {
                return Math.ceil(this.filteredData.length / this.perPage) || 1;
            },
            displayedPages() {
                const pages = [];
                const total = this.totalPages;
                const current = this.currentPage;
                if (total <= 7) {
                    for (let i = 1; i <= total; i++) pages.push(i);
                } else {
                    if (current <= 3) {
                        pages.push(1, 2, 3, 4, '...', total);
                    } else if (current >= total - 2) {
                        pages.push(1, '...', total - 3, total - 2, total - 1, total);
                    } else {
                        pages.push(1, '...', current - 1, current, current + 1, '...', total);
                    }
                }
                return pages;
            },
            paginationStart() {
                return this.filteredData.length === 0 ? 0 : (this.currentPage - 1) * this.perPage + 1;
            },
            paginationEnd() {
                return Math.min(this.currentPage * this.perPage, this.filteredData.length);
            },
        },

        watch: {
            searchQuery() { this.currentPage = 1; },
            perPage() { this.currentPage = 1; },
        },

        created() {
            this.handleSearchDebounced = debounce(this.handleSearch, 300);
        },

        methods: {
            show(title, docType, docCategory) {
                this.titleModal = title;
                this.documentType = docType;
                this.documentCategory = docCategory;
                this.resetState();
                this.setPermissions();
                this.loadCustomLabels();
                this.loadInputLabelForms();
                this.loadOtherData();
                this.loadData();
                this.isVisible = true;
                document.body.style.overflow = 'hidden';
            },

            close() {
                this.isVisible = false;
                document.body.style.overflow = '';
                this.closeAllMenus();
            },

            resetState() {
                this.searchQuery = '';
                this.selectedPeriod = '1d';
                this.currentPage = 1;
                this.dataTable = [];
            },

            toggleMobileMenu() {
                this.showMobileMenu = !this.showMobileMenu;
                this.showDesktopMenu = false;
            },

            toggleDesktopMenu() {
                this.showDesktopMenu = !this.showDesktopMenu;
                this.showMobileMenu = false;
            },

            closeAllMenus() {
                this.showMobileMenu = false;
                this.showDesktopMenu = false;
                this.showMobileActionSheet = false;
            },

            handleOverlayClick() {
                if (this.showMobileMenu || this.showDesktopMenu) {
                    this.closeAllMenus();
                }
            },

            openMobileActions(item) {
                this.selectedItem = item;
                this.showMobileActionSheet = true;
            },

            closeMobileActions() {
                this.showMobileActionSheet = false;
                this.selectedItem = null;
            },

            // ── Menus (charte : un seul menu ouvert + clic extérieur) ──
            toggleMenu(key) {
                this.openMenu = this.openMenu === key ? null : key;
            },

            handleGlobalClick(e) {
                if (!e.target.closest('.ti-btn-group')
                    && !e.target.closest('.ag-menu')
                    && !e.target.closest('.ds-btn-ghost')) {
                    this.openMenu = null;
                }
            },

            async loadData() {
                this.isLoading = true;
                try {
                    const url = `${process.env.VUE_APP_API_URL}/jkebfgescomporziprodpfachatventeegl745874stock771714dfe/gescom/default/all/${this.documentCategory}/${this.documentType}/${this.selectedPeriod}/${this.$store.state.appsociete}/${randomString()}`;
                    const response = await axios.get(url, {
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest',
                            'Accept': 'application/json',
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${JwtService.getToken()}`,
                            'csrftoken': this.$store.state.appsociete,
                        },
                        withCredentials: true,
                    });
                    this.dataTable = response.data.success ? response.data.m || [] : [];
                } catch (error) {
                    console.error('Error loading data:', error);
                    this.dataTable = [];
                } finally {
                    this.isLoading = false;
                }
            },

            async loadOtherData() {
                try {
                    const url = `${process.env.VUE_APP_API_URL}/jkebfiuebffeznfoieznofibeubqsyudfyegl745874stock771714dfe/gescom/othetdskd8755dsdhisd/${this.documentCategory}/${this.$store.state.appsociete}/${randomString()}`;
                    const response = await axios.get(url, {
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest',
                            'Accept': 'application/json',
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${JwtService.getToken()}`,
                            'csrftoken': this.$store.state.appsociete,
                        },
                        withCredentials: true,
                    });
                    if (response.data.success) {
                        this.otherData = response.data.otherdata || [];
                    }
                } catch (error) {
                    console.error('Error loading other data:', error);
                }
            },

            loadCustomLabels() {
                const storedLabels = JwtService.getCUSTOMLIBELLE();
                if (isset(storedLabels)) {
                    this.customLabelForms = JSON.parse(storedLabels);
                } else {
                    this.customLabelForms = this.$store.state.appcustomlibelleachat;
                    JwtService.setCUSTOMLIBELLE(JSON.stringify(this.customLabelForms));
                }
            },

            async loadInputLabelForms() {
                const storedLabels = JwtService.getVALUEDATAPRICE();
                if (isset(storedLabels)) {
                    this.inputLabelForms = JSON.parse(storedLabels);
                } else {
                    try {
                        const url = `${process.env.VUE_APP_API_URL}/12478855libelinputindex2iofdofiloadexp2/${this.$store.state.appsociete}/${randomString()}`;
                        const response = await axios.get(url, {
                            headers: {
                                'X-Requested-With': 'XMLHttpRequest',
                                'Accept': 'application/json',
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${JwtService.getToken()}`,
                                'csrftoken': this.$store.state.appsociete,
                            },
                            withCredentials: true,
                        });
                        if (response.data.success) {
                            this.inputLabelForms = response.data.valuedataprice;
                            JwtService.setVALUEDATAPRICE(JSON.stringify(this.inputLabelForms));
                        }
                    } catch (error) {
                        console.error('Error loading input labels:', error);
                    }
                }
            },

            refreshData() {
                this.showDesktopMenu = false;
                this.loadData();
            },

            handleSearch() {},
            clearSearch() { this.searchQuery = ''; },

            handlePeriodChange(period) {
                this.selectedPeriod = period;
                this.currentPage = 1;
                this.loadData();
            },

            handleSort(column) {
                if (this.sortBy === column) {
                    this.sortDesc = !this.sortDesc;
                } else {
                    this.sortBy = column;
                    this.sortDesc = true;
                }
            },

            setPermissions() {

                const userPermissions = this.$store.state.currentuserpermissions;

                // Valeurs par défaut (sécurité)
                this.canCreate = false;
                this.canUpdate = false;
                this.canDelete = false;

                const permissionMap = {
                    DAF: {
                        add: achatsdemandeadd,
                        edit: achatsdemandeedit,
                        delete: achatsdemandedelete
                    },
                    BCF: {
                        add: achatscommandeadd,
                        edit: achatscommandeedit,
                        delete: achatscommandedelete
                    },
                    BRF: {
                        add: achatsreceptionadd,
                        edit: achatsreceptionedit,
                        delete: achatsreceptiondelete
                    },
                    FR: {
                        add: achatsfactureadd,
                        edit: achatsfactureedit,
                        delete: achatsfacturedelete
                    },
                    RF: {
                        add: achatsretouradd,
                        edit: achatsretouredit,
                        delete: achatsretourdelete
                    },
                    FRG: {
                        add: achatsconsolidationfactureadd,
                        edit: achatsconsolidationfactureedit,
                        delete: achatsconsolidationfacturedelete
                    },
                    PF: {
                        add: ventesdevisadd,
                        edit: ventesdevisedit,
                        delete: ventesdevisdelete
                    },
                    BC: {
                        add: ventescommandeadd,
                        edit: ventescommandeedit,
                        delete: ventescommandedelete
                    },
                    BPL: {
                        add: ventespreparationadd,
                        edit: ventespreparationedit,
                        delete: ventespreparationdelete
                    },
                    BL: {
                        add: venteslivraisonadd,
                        edit: venteslivraisonedit,
                        delete: venteslivraisondelete
                    },
                    F: {
                        add: ventesfactureadd,
                        edit: ventesfactureedit,
                        delete: ventesfacturedelete
                    },
                    BR: {
                        add: ventesavoiradd,
                        edit: ventesavoiredit,
                        delete: ventesavoirdelete
                    },
                    FG: {
                        add: ventesconsolidationfactureventeadd,
                        edit: ventesconsolidationfactureventeedit,
                        delete: ventesconsolidationfactureventedelete
                    },
                };

                const config = permissionMap[this.documentType];


                // Si le type n'est pas mappé, on s'arrête ici
                if (!config) return;

                // Permissions standards
                this.canCreate = permissionHelper.check(config.add, userPermissions);
                this.canUpdate = permissionHelper.check(config.edit, userPermissions);
                this.canDelete = permissionHelper.check(config.delete, userPermissions);
            },


            canNormalize(item) {
                return item.nuse === 0 && item.nbp > 0 && ['F', 'FA', 'BR'].includes(this.documentType);
            },

            handleAdd() {
                const titleMap = {
                    DAF: "Enregistrer une demande d'achat",
                    BCF: 'Enregistrer un bon de commande fournisseur',
                    BRF: 'Enregistrer un bon de réception fournisseur',
                    PF: 'Enregistrer un devis',
                    BC: 'Enregistrer un bon de commande client',
                    BPL: 'Enregistrer un bon de préparation de livraison',
                    BL: 'Enregistrer un bon de livraison clients',
                    F: 'Enregistrer une facture clients',
                    BR: 'Enregistrer un bon de retour clients',
                };
                const item = this.createEmptyItem();
                this.$refs.modalform.show(
                    titleMap[this.documentType] || 'Nouveau document',
                    'add',
                    this.documentType,
                    item,
                    this.otherData,
                    this.inputLabelForms,
                    this.documentCategory,
                    this.customLabelForms
                );
            },

            createEmptyItem() {
                return {
                    pid: '',
                    mag: null,
                    datedoc: this.$store.state.appserverdate,
                    docobservation: '',
                    docnum: '',
                    docnumother: '',
                    docpid: 0,
                    doctyp: this.documentType,
                    infodoc: { infolibre: '', infolibre1: '', infolibre2: '', infolibre3: '' },
                    jsonotherdonne: {
                        datedelivraison: '',
                        dateechancefacture: '',
                        tpdoc: '',
                        tredocument: '',
                        magvers: null,
                        mtypprixdevente: 'prixvente',
                        collaborateur: null,
                        cltfrs: null,
                        moderglt: null,
                        cdtreglement: null,
                        appliquetva: 1,
                        aib: 0,
                        adresseexpedition: { infolibre: '', infolibre1: '', infolibre2: '', infolibre3: '' },
                        infodoc: { infolibre: '', infolibre1: '', infolibre2: '', infolibre3: '', infolibre4: '', infolibre5: '', infolibre6: '', infolibre7: '', infolibre8: '', infolibre9: '', infolibre10: '', infolibre11: '' },
                        piedsinfodoc: { infolibre: '', infolibre1: '', infolibre2: '', infolibre3: '' },
                    },
                    mtnt: { docmtntaib: '0', docmtntremise: '0', docmtnttotalht: '0', docmtnttotalttc: '0', doctva: '0', doccalmtnttotalht: '0', libaib: 'AIB' },
                    params: { param1: null, param2: null, param3: null, param4: null },
                    jsondoc: {},
                };
            },

            handleOpen(item) {
                this.$refs.modalopenform.showupdate(`Document ${item.numdoc}`, 'update', this.documentType, item, this.otherData);
            },

            handleEdit(item) {
                this.$refs.modalform.showupdate(`Modifier le document ${item.numdoc}`, 'update', this.documentType, item, this.otherData, this.inputLabelForms, this.documentCategory, this.customLabelForms);
            },

            handleDelete(item) {
                if (item.useline === 1 || item.useline === '1') {
                    this.$toast?.error('Impossible de supprimer cette ligne.');
                    return;
                }
                this.$refs.modalformsuppression.show(`VOULEZ-VOUS VRAIMENT SUPPRIMER LE DOCUMENT ${item.numdoc} ?`, item, this.documentType, this.documentCategory);
            },

            handleTransform(item) {
                this.$refs.modaltransformation.show('', item, this.documentType, this.documentCategory);
            },

            handleNormalize(item) {
                this.$refs.normalisationdelafacture.show(item, this.documentType, this.documentCategory);
            },

            handlePrint_old(item) {
                this.$refs.modalimpressiondoc.show(item);
            },





            async handlePrint(value){

                //this.$refs.modalimpressiondoc.show(value);

                this.$store.dispatch('lancerchargement',true)
                const t = this;
                let url=process.env.VUE_APP_API_URL+'/jkebfiuebffeznfoieznofibeubqsyudfyegl745874stock771714dfe/stock/'+value.numdoc+'/'+value.pid+'/open/'+this.$store.state.appsociete+'/'+randomString();
                let response =   axios  .get(url,{
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest',
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + JwtService.getToken(),
                        'csrftoken': this.$store.state.appsociete
                    } ,withCredentials: true // Permet l'envoi de cookies et autres informations d'authentification
                })

                let res= await response
                    .then(function (res){
                        t.$store.dispatch('lancerchargement',false)


                        if(res.data.success){


                            value.alllignedoc=res.data.alllignedoc;

                            t.callimprimer(value);
                        }
                        else{
                            t.listedatatableproduit=[];
                            // t.isdisplay=true;

                        }


                    })
                    .catch((error)=>{

                            t.$store.dispatch('lancerchargement',false)
                        }
                    )
                    .finally(() =>{

                        t.$store.dispatch('lancerchargement',false)


                    })



            },



            callimprimer(value) {



                let   tabvalue={}



                tabvalue={
                    jsoncltfrs:{
                        typcltfrs:'',
                        raisonsociale:value.jsonotherdonne.cltfrs.name,
                        ifu:value.jsonotherdonne.cltfrs.ifu,
                        telephone:value.jsonotherdonne.cltfrs.telephone,
                        adresse:value.jsonotherdonne.cltfrs.adresse,
                        codeclient:value.jsonotherdonne.cltfrs.codecltfrs
                    },
                    jsoncol:{
                        name:'',
                    },
                    titresignataire:"",
                    nomdusignataire:"",
                    texteavance:String(value.jsonotherdonne.piedsinfodoc.infolibre),
                    textesole:String(value.jsonotherdonne.piedsinfodoc.infolibre1),
                    infolibre2:String(value.jsonotherdonne.piedsinfodoc.infolibre2),

                    infolibre:String(value.jsonotherdonne.infodoc.infolibre),
                    infolibre1:String(value.jsonotherdonne.infodoc.infolibre1),
                    infolibre22:String(value.jsonotherdonne.infodoc.infolibre2),
                    infolibre3:String(value.jsonotherdonne.infodoc.infolibre3),
                    infolibre4:String(value.jsonotherdonne.infodoc.infolibre4),
                    infolibre5:String(value.jsonotherdonne.infodoc.infolibre5),
                    infolibre6:String(value.jsonotherdonne.infodoc.infolibre6),
                    infolibre7:String(value.jsonotherdonne.infodoc.infolibre7),
                    infolibre8:String(value.jsonotherdonne.infodoc.infolibre8),
                    infolibre9:String(value.jsonotherdonne.infodoc.infolibre9),
                    infolibre10:String(value.jsonotherdonne.infodoc.infolibre10),
                    infolibre11:String(value.jsonotherdonne.infodoc.infolibre11),

                    libelledefacture:value.doctyp,
                    cltfrs:value.jsonotherdonne.cltfrs.name,
                    numdoc:value.numdoc,
                    operateur:value.saisiepar,
                    othernumdoc:value.numbon,

                    observations:value.mobservations,
                    date:value.datedoc,
                    items:value.alllignedoc,
                    montantht:parseFloat(value.mtnt.docmtnttotalht),
                    montanttva:parseFloat(value.mtnt.doctva),
                    montantremise:parseFloat(value.mtnt.docmtntremise),
                    montantnetapayer:parseFloat(value.mtnt.docmtnttotalttc),
                    montantaib:parseFloat(value.mtnt.docmtntaib),
                    libaib:value.mtnt.libaib,

                    formattext:'',

                    mecefsignature:'',
                    mecefnim:'',
                    mecefcompteur:'',
                    mecefheure:'',
                    mecefqrr:'',
                    normalise:0,


                    /************************** signature ****************/

                    signature:{
                        titredoc:"",
                        titresignataire:"",
                        namesignataire:"",
                        imgsignature:"",
                        datasignataires:[],
                        imgsignataires:[]
                    }




                }

                if(value.doctyp==='PF'){

                    tabvalue.signature.titredoc=this.$store.state.paramsapp.signataire.ventedevis.titredoc
                    tabvalue.signature.datasignataires=this.$store.state.paramsapp.signataire.ventedevis.signataires


                    tabvalue.jsoncltfrs.typcltfrs="CLIENT";

                    if (parseInt(this.$store.state.paramsapp.signataire.ventedevis.document)===0){
                        this.$refs.vente_devis_etat0.show(tabvalue);
                    }


                    if (parseInt(this.$store.state.paramsapp.signataire.ventedevis.document)===1){
                        this.$refs.vente_devis_etat1.show(tabvalue);
                    }


                    if (parseInt(this.$store.state.paramsapp.signataire.ventedevis.document)===2){
                        this.$refs.vente_devis_etat2.show(tabvalue);
                    }


                    if (parseInt(this.$store.state.paramsapp.signataire.ventedevis.document)===3){
                        this.$refs.vente_devis_etat3.show(tabvalue);
                    }


                    if (parseInt(this.$store.state.paramsapp.signataire.ventedevis.document)===4){
                        this.$refs.vente_devis_etat4.show(tabvalue);
                    }


                    if (parseInt(this.$store.state.paramsapp.signataire.ventedevis.document)===5){
                        this.$refs.vente_devis_etat5.show(tabvalue);
                    }



                }


                if(value.doctyp==='BC'){

                    tabvalue.signature.titredoc=this.$store.state.paramsapp.signataire.ventebcd.titredoc
                    tabvalue.signature.datasignataires=this.$store.state.paramsapp.signataire.ventebcd.signataires


                    tabvalue.jsoncltfrs.typcltfrs="CLIENT";

                    if (parseInt(this.$store.state.paramsapp.signataire.ventebcd.document)===0){
                        this.$refs.vente_bcd_etat0.show(tabvalue);
                    }

                    if (parseInt(this.$store.state.paramsapp.signataire.ventebcd.document)===1){
                        this.$refs.vente_bcd_etat1.show(tabvalue);
                    }

                    if (parseInt(this.$store.state.paramsapp.signataire.ventebcd.document)===2){
                        this.$refs.vente_bcd_etat2.show(tabvalue);
                    }

                    if (parseInt(this.$store.state.paramsapp.signataire.ventebcd.document)===3){
                        this.$refs.vente_bcd_etat3.show(tabvalue);
                    }

                    if (parseInt(this.$store.state.paramsapp.signataire.ventebcd.document)===4){
                        this.$refs.vente_bcd_etat4.show(tabvalue);
                    }

                    if (parseInt(this.$store.state.paramsapp.signataire.ventebcd.document)===5){
                        this.$refs.vente_bcd_etat5.show(tabvalue);
                    }



                }


                if(value.doctyp==='BPL'){

                    tabvalue.jsoncltfrs.typcltfrs="CLIENT";

                    if (parseInt(this.$store.state.paramsapp.signataire.ventebpl.document)===0){
                        this.$refs.vente_bpl_etat0.show(tabvalue);
                    }

                    if (parseInt(this.$store.state.paramsapp.signataire.ventebpl.document)===1){

                        tabvalue.jsoncol.name=isset(value.jsonotherdonne.collaborateur)?value.jsonotherdonne.collaborateur.name:"";
                        let formattext='';

                        value.alllignedoc.forEach(function (line) {


                            if (formattext===''){


                                formattext=line.qtite+' '+line.article.libelle;

                            }else{

                                formattext+=', '+line.qtite+' '+line.article.libelle;

                            }

                        })

                        tabvalue.formattext=formattext;



                        tabvalue.signature.titredoc=this.$store.state.paramsapp.signataire.ventebpl.titredoc
                        tabvalue.signature.datasignataires=this.$store.state.paramsapp.signataire.ventebpl.signataires




                        this.$refs.vente_bpl_etat1.show(tabvalue);
                    }

                    if (parseInt(this.$store.state.paramsapp.signataire.ventebpl.document)===2){
                        this.$refs.vente_bpl_etat2.show(tabvalue);
                    }

                    if (parseInt(this.$store.state.paramsapp.signataire.ventebpl.document)===3){
                        this.$refs.vente_bpl_etat3.show(tabvalue);
                    }

                    if (parseInt(this.$store.state.paramsapp.signataire.ventebpl.document)===4){
                        this.$refs.vente_bpl_etat4.show(tabvalue);
                    }

                    if (parseInt(this.$store.state.paramsapp.signataire.ventebpl.document)===5){
                        this.$refs.vente_bpl_etat5.show(tabvalue);
                    }



                }

                if(value.doctyp==='BL'){
                    tabvalue.jsoncltfrs.typcltfrs="CLIENT";

                    tabvalue.signature.titredoc=this.$store.state.paramsapp.signataire.ventebl.titredoc
                    tabvalue.signature.datasignataires=this.$store.state.paramsapp.signataire.ventebl.signataires



                    if (parseInt(this.$store.state.paramsapp.signataire.ventebl.document)===0){
                        this.$refs.vente_bl_etat0.show(tabvalue);
                    }


                    if (parseInt(this.$store.state.paramsapp.signataire.ventebl.document)===1){


                        tabvalue.jsoncol.name=value.jsonotherdonne.collaborateur.name;
                        let formattext='';

                        value.alllignedoc.forEach(function (line) {


                            if (formattext===''){


                                formattext=line.qtite+' '+line.article.libelle;

                            }else{

                                formattext+=', '+line.qtite+' '+line.article.libelle;

                            }

                        })

                        tabvalue.formattext=formattext;






                        this.$refs.vente_bl_etat1.show(tabvalue);
                    }


                    if (parseInt(this.$store.state.paramsapp.signataire.ventebl.document)===2){
                        this.$refs.vente_bl_etat2.show(tabvalue);
                    }


                    if (parseInt(this.$store.state.paramsapp.signataire.ventebl.document)===3){
                        this.$refs.vente_bl_etat3.show(tabvalue);
                    }


                    if (parseInt(this.$store.state.paramsapp.signataire.ventebl.document)===4){
                        this.$refs.vente_bl_etat4.show(tabvalue);
                    }


                    if (parseInt(this.$store.state.paramsapp.signataire.ventebl.document)===5){
                        this.$refs.vente_bl_etat5.show(tabvalue);
                    }



                }


                if(value.doctyp==='F'){

                    tabvalue.signature.titredoc=this.$store.state.paramsapp.signataire.ventefacture.titredoc
                    tabvalue.signature.datasignataires=this.$store.state.paramsapp.signataire.ventefacture.signataires



                    tabvalue.jsoncltfrs.typcltfrs="CLIENT";
                    if (value.infodoc.normalise===1){

                        let urlimagegenerer="";

                        // Générer un QR code sous forme d'URL de données
                        qrcodetoimage.toDataURL(value.infodoc.doccollecte.data.qrCode, { width: 125, margin: 2 }, (err, url) => {
                            if (err) {
                                console.error(err);
                            } else {



                                urlimagegenerer=  url;
                            }
                        });

                        tabvalue.mecefsignature=value.infodoc.doccollecte.data.codeMECeFDGI
                        tabvalue.mecefnim=value.infodoc.doccollecte.data.nim
                        tabvalue.mecefcompteur=value.infodoc.doccollecte.data.counters
                        tabvalue.mecefheure=value.infodoc.doccollecte.data.dateTime
                        //tabvalue.mecefqrr=value.infodoc.doccollecte.data.qrCode
                        tabvalue.mecefqrr=urlimagegenerer ,
                            tabvalue.normalise=value.infodoc.normalise
                    }


                    if (parseInt(this.$store.state.paramsapp.signataire.ventefacture.document)===0){

                        this.$refs.vente_facture_etat0.show(tabvalue);

                    }


                    if (parseInt(this.$store.state.paramsapp.signataire.ventefacture.document)===1){


                        this.$refs.vente_facture_etat1.show(tabvalue);

                    }


                    if (parseInt(this.$store.state.paramsapp.signataire.ventefacture.document)===2){

                        this.$refs.vente_facture_etat2.show(tabvalue);

                    }


                    if (parseInt(this.$store.state.paramsapp.signataire.ventefacture.document)===3){

                        this.$refs.vente_facture_etat3.show(tabvalue);

                    }


                    if (parseInt(this.$store.state.paramsapp.signataire.ventefacture.document)===4){

                        this.$refs.vente_facture_etat4.show(tabvalue);

                    }


                    if (parseInt(this.$store.state.paramsapp.signataire.ventefacture.document)===5){

                        this.$refs.vente_facture_etat5.show(tabvalue);

                    }



                }


                if(value.doctyp==='BR'){



                    tabvalue.jsoncltfrs.typcltfrs="CLIENT";

                    tabvalue.signature.titredoc=this.$store.state.paramsapp.signataire.venteretour.titredoc
                    tabvalue.signature.datasignataires=this.$store.state.paramsapp.signataire.venteretour.signataires



                    if (value.infodoc.normalise===1){


                        let urlimagegenerer="";

                        // Générer un QR code sous forme d'URL de données
                        qrcodetoimage.toDataURL(value.infodoc.doccollecte.data.qrCode, { width: 125, margin: 2 }, (err, url) => {
                            if (err) {
                                console.error(err);
                            } else {

                                urlimagegenerer=  url;
                            }
                        });


                        tabvalue.mecefsignature=value.infodoc.doccollecte.data.codeMECeFDGI
                        tabvalue.mecefnim=value.infodoc.doccollecte.data.nim
                        tabvalue.mecefcompteur=value.infodoc.doccollecte.data.counters
                        tabvalue.mecefheure=value.infodoc.doccollecte.data.dateTime
                        //tabvalue.mecefqrr=value.infodoc.doccollecte.data.qrCode
                        tabvalue.mecefqrr=urlimagegenerer ,
                            tabvalue.normalise=value.infodoc.normalise

                    }




                    if (parseInt(this.$store.state.paramsapp.signataire.venteretour.document)===0){
                        this.$refs.vente_retour_etat0.show(tabvalue);
                    }


                    if (parseInt(this.$store.state.paramsapp.signataire.venteretour.document)===1){
                        this.$refs.vente_retour_etat1.show(tabvalue);
                    }


                    if (parseInt(this.$store.state.paramsapp.signataire.venteretour.document)===2){
                        this.$refs.vente_retour_etat2.show(tabvalue);
                    }


                    if (parseInt(this.$store.state.paramsapp.signataire.venteretour.document)===3){
                        this.$refs.vente_retour_etat3.show(tabvalue);
                    }


                    if (parseInt(this.$store.state.paramsapp.signataire.venteretour.document)===4){
                        this.$refs.vente_retour_etat4.show(tabvalue);
                    }


                    if (parseInt(this.$store.state.paramsapp.signataire.venteretour.document)===5){
                        this.$refs.vente_retour_etat5.show(tabvalue);
                    }



                }

                //**************************   ACHAT   **************************************//

                if(value.doctyp==='DAF'){

                    tabvalue.jsoncltfrs.typcltfrs="FOURNISSEUR";

                    tabvalue.signature.titredoc=this.$store.state.paramsapp.signataire.demandedachat.titredoc
                    tabvalue.signature.datasignataires=this.$store.state.paramsapp.signataire.demandedachat.signataires


                    if (parseInt(this.$store.state.paramsapp.signataire.demandedachat.document)===0){
                        this.$refs.achat_demandedachat_etat0.show(tabvalue);
                    }

                    if (parseInt(this.$store.state.paramsapp.signataire.demandedachat.document)===1){
                        this.$refs.achat_demandedachat_etat1.show(tabvalue);
                    }

                    if (parseInt(this.$store.state.paramsapp.signataire.demandedachat.document)===2){
                        this.$refs.achat_demandedachat_etat2.show(tabvalue);
                    }

                    if (parseInt(this.$store.state.paramsapp.signataire.demandedachat.document)===3){
                        this.$refs.achat_demandedachat_etat3.show(tabvalue);
                    }

                    if (parseInt(this.$store.state.paramsapp.signataire.demandedachat.document)===4){
                        this.$refs.achat_demandedachat_etat4.show(tabvalue);
                    }

                    if (parseInt(this.$store.state.paramsapp.signataire.demandedachat.document)===5){
                        this.$refs.achat_demandedachat_etat5.show(tabvalue);
                    }



                }


                if(value.doctyp==='BCF'){

                    tabvalue.signature.titredoc=this.$store.state.paramsapp.signataire.achatbcd.titredoc
                    tabvalue.signature.datasignataires=this.$store.state.paramsapp.signataire.achatbcd.signataires


                    tabvalue.jsoncltfrs.typcltfrs="FOURNISSEUR / SUPPLIER";

                    if (parseInt(this.$store.state.paramsapp.signataire.achatbcd.document)===0){
                        this.$refs.achat_bcd_etat0.show(tabvalue);
                    }

                    if (parseInt(this.$store.state.paramsapp.signataire.achatbcd.document)===1){

                        this.$refs.achat_bcd_etat1.show(tabvalue);
                    }

                    if (parseInt(this.$store.state.paramsapp.signataire.achatbcd.document)===2){
                        this.$refs.achat_bcd_etat2.show(tabvalue);
                    }

                    if (parseInt(this.$store.state.paramsapp.signataire.achatbcd.document)===3){
                        this.$refs.achat_bcd_etat3.show(tabvalue);
                    }

                    if (parseInt(this.$store.state.paramsapp.signataire.achatbcd.document)===4){
                        this.$refs.achat_bcd_etat4.show(tabvalue);
                    }

                    if (parseInt(this.$store.state.paramsapp.signataire.achatbcd.document)===5){
                        this.$refs.achat_bcd_etat5.show(tabvalue);
                    }



                }


                if(value.doctyp==='BRF'){

                    tabvalue.signature.titredoc=this.$store.state.paramsapp.signataire.achatbondereception.titredoc
                    tabvalue.signature.datasignataires=this.$store.state.paramsapp.signataire.achatbondereception.signataires


                    tabvalue.jsoncltfrs.typcltfrs="FOURNISSEUR";

                    if (parseInt(this.$store.state.paramsapp.signataire.achatbondereception.document)===0){
                        this.$refs.achat_bondereception_etat0.show(tabvalue);
                    }

                    if (parseInt(this.$store.state.paramsapp.signataire.achatbondereception.document)===1){
                        this.$refs.achat_bondereception_etat1.show(tabvalue);
                    }

                    if (parseInt(this.$store.state.paramsapp.signataire.achatbondereception.document)===2){
                        this.$refs.achat_bondereception_etat2.show(tabvalue);
                    }

                    if (parseInt(this.$store.state.paramsapp.signataire.achatbondereception.document)===3){
                        this.$refs.achat_bondereception_etat3.show(tabvalue);
                    }

                    if (parseInt(this.$store.state.paramsapp.signataire.achatbondereception.document)===4){
                        this.$refs.achat_bondereception_etat4.show(tabvalue);
                    }

                    if (parseInt(this.$store.state.paramsapp.signataire.achatbondereception.document)===5){
                        this.$refs.achat_bondereception_etat5.show(tabvalue);
                    }



                }


                if(value.doctyp==='FR'){

                    tabvalue.jsoncltfrs.typcltfrs="FOURNISSEUR";

                    tabvalue.signature.titredoc=this.$store.state.paramsapp.signataire.achatfacture.titredoc
                    tabvalue.signature.datasignataires=this.$store.state.paramsapp.signataire.achatfacture.signataires


                    if (parseInt(this.$store.state.paramsapp.signataire.achatfacture.document)===0){
                        this.$refs.achat_facture_etat0.show(tabvalue);
                    }


                    if (parseInt(this.$store.state.paramsapp.signataire.achatfacture.document)===1){
                        this.$refs.achat_facture_etat1.show(tabvalue);
                    }


                    if (parseInt(this.$store.state.paramsapp.signataire.achatfacture.document)===2){
                        this.$refs.achat_facture_etat2.show(tabvalue);
                    }


                    if (parseInt(this.$store.state.paramsapp.signataire.achatfacture.document)===3){
                        this.$refs.achat_facture_etat3.show(tabvalue);
                    }


                    if (parseInt(this.$store.state.paramsapp.signataire.achatfacture.document)===4){
                        this.$refs.achat_facture_etat4.show(tabvalue);
                    }


                    if (parseInt(this.$store.state.paramsapp.signataire.achatfacture.document)===5){
                        this.$refs.achat_facture_etat5.show(tabvalue);
                    }



                }


                if(value.doctyp==='RF'){

                    tabvalue.jsoncltfrs.typcltfrs="FOURNISSEUR";

                    tabvalue.signature.titredoc=this.$store.state.paramsapp.signataire.achatretour.titredoc
                    tabvalue.signature.datasignataires=this.$store.state.paramsapp.signataire.achatretour.signataires


                    if (parseInt(this.$store.state.paramsapp.signataire.achatretour.document)===0){
                        this.$refs.achat_retour_etat0.show(tabvalue);
                    }

                    if (parseInt(this.$store.state.paramsapp.signataire.achatretour.document)===1){
                        this.$refs.achat_retour_etat1.show(tabvalue);
                    }

                    if (parseInt(this.$store.state.paramsapp.signataire.achatretour.document)===2){
                        this.$refs.achat_retour_etat2.show(tabvalue);
                    }

                    if (parseInt(this.$store.state.paramsapp.signataire.achatretour.document)===3){
                        this.$refs.achat_retour_etat3.show(tabvalue);
                    }

                    if (parseInt(this.$store.state.paramsapp.signataire.achatretour.document)===4){
                        this.$refs.achat_retour_etat4.show(tabvalue);
                    }

                    if (parseInt(this.$store.state.paramsapp.signataire.achatretour.document)===5){
                        this.$refs.achat_retour_etat5.show(tabvalue);
                    }



                }








            },




            handleCustomizeLabels() {
                this.closeAllMenus();
                this.$refs.modalcustomlib.show(this.customLabelForms, 'achat');
            },

            handleCustomLibSelected(selection) {
                this.customLabelForms = selection.value;
            },

            handleTransformationSelected(selection) {
                if (selection.typ === '1') {
                    this.documentType = selection.mynewtypdoc;
                    this.setPermissions();
                    this.selectedPeriod = '1d';

                    if (this.documentType==='DAF'){
                        this.titleModal="Liste des demandes d'achat"
                    }

                    if (this.documentType==='BCF'){
                        this.titleModal="Liste des bons de commande fournisseur"
                    }

                    if (this.documentType==='BRF'){
                        this.titleModal="Liste des bons de réception fournisseur"
                    }

                    if (this.documentType==='FR'){
                        this.titleModal="Liste des factures fournisseur"
                    }

                    if (this.documentType==='FRG'){
                        this.titleModal="Liste des factures consolidées (fournisseur)"
                    }

                    if (this.documentType==='RF'){
                        this.titleModal="Liste des bons de retour fournisseur"
                    }

                    if (this.documentType==='PF'){
                        this.titleModal="Liste des devis"
                    }

                    if (this.documentType==='BC'){
                        this.titleModal="Liste des bon de commande client"
                    }

                    if (this.documentType==='BPL'){
                        this.titleModal="Liste des bons de préparation de livraison"
                    }

                    if (this.documentType==='BL'){
                        this.titleModal="Liste des bons de livraisson clients"
                    }

                    if (this.documentType==='F'){
                        this.titleModal="Liste des factures clients"
                    }


                    if (this.documentType==='FG'){
                        this.titleModal="Liste des factures consolidées (clients)"
                    }

                    if (this.documentType==='BR'){
                        this.titleModal="Liste des bons de retour clients"
                    }

                    if (this.documentType==='FA'){
                        this.titleModal="Liste des factures d'avoir clients"
                    }



                    this.loadtransformation(selection.keypid)

                   // this.loadData();
                }
            },

            btnmodifier(value){

                let titlemodal="Modifier le document "+value.numdoc

                let Item=value



                this.$refs.modalform.showupdate(titlemodal,"update",this.documentType,Item,this.otherData,this.inputLabelForms,this.documentCategory,this.customLabelForms);


            },


            async loadtransformation($keysearch){



                this.$store.dispatch('lancerchargement',true)
                const t = this;
                let url=process.env.VUE_APP_API_URL+'/jkebfgescomporziprodpfachatventeegl745874stock771714dfe/gescom/default/all/'+this.documentCategory+'/'+this.documentType+'/'+this.selectedPeriod+'/'+this.$store.state.appsociete+'/'+randomString();
                let response =   axios  .get(url,{
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest',
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + JwtService.getToken(),
                        'csrftoken': this.$store.state.appsociete
                    } ,withCredentials: true // Permet l'envoi de cookies et autres informations d'authentification
                })

                let res= await response
                    .then(function (res){

                        if(res.data.success){


                            t.dataTable=[];
                            t.dataTable=res.data.m;



                            let $chekvalue={};



                            for (let $line of t.dataTable){

                                if ($line.pid===$keysearch){
                                    $chekvalue=$line
                                    break;
                                }

                            }

                            if (Object.keys($chekvalue).length>0){

                                t.btnmodifier($chekvalue)

                            }







                        }
                        else{
                            t.dataTable=[];

                        }


                    })
                    .catch((error)=>{

                            t.$store.dispatch('lancerchargement',false)
                        }
                    )
                    .finally(() =>{

                        t.$store.dispatch('lancerchargement',false)


                    })




            },

            exportData() {
                this.closeAllMenus();
                console.log('Export functionality');
            },

            getInitials(name) {
                if (!name) return '?';
                return name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
            },

            formatDate(dateString) {
                if (!dateString) return '-';
                try {
                    return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(dateString));
                } catch {
                    return dateString;
                }
            },
        },

        mounted() {
            document.addEventListener('click', this.handleGlobalClick)
        },

        beforeUnmount() {
            document.body.style.overflow = '';
            document.removeEventListener('click', this.handleGlobalClick)
        },
    };
</script>

<style scoped>
/* Charte cf-/ds- : tokens uniquement (light-only) */
.cf-overlay { position:fixed; inset:0; z-index:1000; display:flex; align-items:center; justify-content:center; padding:1rem; background:rgb(15 23 42 / 0.45); }
.cf-dialog { width:100%; background:#fff; border-radius:var(--cf-radius); box-shadow:var(--cf-shadow-md); display:flex; flex-direction:column; max-height:94vh; overflow:hidden; }
.cf-dialog--xxl { max-width:1200px; }
.cf-dialog-head { display:flex; align-items:center; justify-content:space-between; padding:1rem 1.25rem; border-bottom:1px solid var(--cf-border); flex-shrink:0; }
.cf-dialog-title { margin:0; font-size:0.95rem; font-weight:700; color:var(--cf-ink-strong); }
.cf-dialog-close { height:2rem; width:2rem; flex-shrink:0; display:inline-flex; align-items:center; justify-content:center; border-radius:var(--cf-radius-sm); border:none; background:var(--cf-surface-alt); color:var(--cf-muted); cursor:pointer; transition:all .15s; }
.cf-dialog-close:hover { background:#fee2e2; color:rgb(var(--danger)); }
.cf-dialog-toolbar { padding:0.75rem 1.25rem; border-bottom:1px solid var(--cf-border); background:var(--cf-surface); flex-shrink:0; }
.cf-dialog-body { padding:1.1rem 1.25rem; overflow-y:auto; flex:1; }
.cf-dialog-body--flush { padding:0; }
.cf-dialog-foot { display:flex; align-items:center; justify-content:flex-end; gap:0.6rem; padding:0.9rem 1.25rem; border-top:1px solid var(--cf-border); background:var(--cf-surface); flex-shrink:0; }
.cf-dialog-foot--split { justify-content:space-between; flex-wrap:wrap; gap:0.6rem; }

/* Toolbar */
.cf-toolbar-row { display:flex; gap:0.75rem; align-items:center; }
.search-wrap { position:relative; flex:1; }
.search-icon { position:absolute; left:12px; top:50%; transform:translateY(-50%); color:var(--cf-faint); }
.search-input { width:100%; padding:9px 34px; border:1px solid rgb(var(--input-border,203 213 225)); border-radius:var(--cf-radius-sm); font-size:0.82rem; color:var(--cf-ink); outline:none; transition:border-color 0.2s; }
.search-input:focus { border-color:rgb(var(--primary)); box-shadow:0 0 0 3px rgb(var(--primary) / 0.10); }
.search-clear { position:absolute; right:8px; top:50%; transform:translateY(-50%); background:none; border:none; cursor:pointer; color:var(--cf-faint); }
.cf-toolbar-meta { display:flex; align-items:center; justify-content:space-between; margin-top:0.7rem; gap:0.75rem; flex-wrap:wrap; }
.cf-period { display:inline-flex; gap:2px; background:var(--cf-surface-alt); padding:3px; border-radius:var(--cf-radius-sm); flex-wrap:wrap; }
.cf-period-btn { padding:5px 12px; font-size:0.76rem; font-weight:600; color:var(--cf-muted); background:none; border:none; border-radius:calc(var(--cf-radius-sm) - 1px); cursor:pointer; transition:all 0.15s; }
.cf-period-btn:hover { color:var(--cf-ink-strong); }
.cf-period-btn--active { background:#fff; color:rgb(var(--primary)); box-shadow:0 1px 2px rgb(15 23 42 / 0.08); }
.cf-perpage { display:flex; align-items:center; gap:0.4rem; font-size:0.8rem; color:var(--cf-muted); }
.cf-select-wrap { position:relative; }
.cf-select--sm { appearance:none; height:34px; padding:0 28px 0 10px; border:1px solid rgb(var(--input-border,203 213 225)); border-radius:var(--cf-radius-sm); background:#fff; font-size:0.8rem; color:var(--cf-ink); cursor:pointer; }
.cf-select-arrow { position:absolute; right:10px; top:50%; transform:translateY(-50%); color:var(--cf-faint); pointer-events:none; }
.cf-count b { color:var(--cf-ink-strong); }
.cf-spin { display:inline-block; animation:cf-spin 1s linear infinite; }
@keyframes cf-spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }

/* Table */
.table-scroll-wrap { overflow:auto; }
.doc-scroll { max-height:56vh; }
.doc-table { width:100%; text-align:left; font-size:13px; border-collapse:collapse; min-width:720px; }
.th { position:sticky; top:0; z-index:2; padding:0.7rem 1rem; font-weight:600; font-size:0.75rem; color:var(--cf-ink); background:var(--cf-surface-alt); border-bottom:1px solid var(--cf-border); white-space:nowrap; letter-spacing:0.02em; text-align:left; }
.th--center { text-align:center; }
.th--sort { cursor:pointer; user-select:none; }
.th--sort:hover { color:var(--cf-ink-strong); }
.th-inner { display:inline-flex; align-items:center; gap:4px; }
.th-sort-icon { opacity:0.35; }
.table-row { border-bottom:1px solid var(--cf-surface-alt); transition:background-color 0.15s; }
.table-row:hover { background:var(--cf-surface); }
.td { padding:0.6rem 1rem; font-size:0.82rem; color:var(--cf-ink); vertical-align:middle; }
.td--center { text-align:center; }
.row-number { color:var(--cf-faint); font-size:0.75rem; font-variant-numeric:tabular-nums; }
.cf-strong { font-weight:500; color:var(--cf-ink-strong); }
.cf-docnum { display:inline-flex; align-items:center; padding:2px 9px; font-size:0.78rem; font-weight:600; color:rgb(var(--primary)); background:rgb(var(--primary) / 0.10); border-radius:var(--cf-radius-sm); }
.cf-ellip { display:inline-block; max-width:220px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; vertical-align:middle; color:var(--cf-muted); }

/* Client (avatar initiales) */
.doc-client { display:flex; align-items:center; gap:8px; }
.doc-avatar { width:32px; height:32px; flex-shrink:0; display:inline-flex; align-items:center; justify-content:center; border-radius:50%; background:rgb(var(--primary) / 0.10); color:rgb(var(--primary)); font-size:0.72rem; font-weight:700; }
.doc-ellip { max-width:200px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }

/* Menu 3 points */
.ag-menu { position:absolute; top:calc(100% + 4px); right:0; z-index:30; min-width:180px; margin:0; padding:0.3rem; list-style:none;
    background:#fff; border:1px solid var(--cf-border); border-radius:var(--cf-radius-sm); box-shadow:var(--cf-shadow-md); }
.ag-menu-item { display:flex; align-items:center; width:100%; padding:0.45rem 0.6rem; border:0; background:transparent; font-size:0.8rem;
    color:var(--cf-ink); text-align:left; cursor:pointer; border-radius:var(--cf-radius-xs); white-space:nowrap; }
.ag-menu-item:hover { background:var(--cf-surface-alt); }
.ag-menu-item--danger { color:rgb(var(--danger)); }
.ag-menu-item--danger .cf-menu-icon { color:rgb(var(--danger)); }

/* Icon action buttons */
.cf-actions { display:inline-flex; align-items:center; gap:4px; justify-content:center; }
.cf-iconbtn { height:2rem; width:2rem; display:inline-flex; align-items:center; justify-content:center; border:none; border-radius:var(--cf-radius-sm); cursor:pointer; font-size:1rem; transition:all 0.15s; background:var(--cf-surface-alt); color:var(--cf-muted); }
.cf-iconbtn--primary:hover { background:rgb(var(--primary) / 0.12); color:rgb(var(--primary)); }

/* Badges */
.cf-badge { display:inline-flex; align-items:center; padding:2px 9px; font-size:0.72rem; font-weight:600; border-radius:999px; white-space:nowrap; }
.cf-badge--ok { background:#dcfce7; color:#15803d; }
.cf-badge--muted { background:var(--cf-surface-alt); color:var(--cf-muted); }

/* Empty state */
.empty-state { padding:2.5rem 1.5rem; text-align:center; }
.empty-state__icon { font-size:40px; color:var(--cf-faint); margin-bottom:14px; }
.empty-state__title { font-size:0.95rem; font-weight:600; color:var(--cf-ink-strong); margin:0; }
.empty-state__sub { font-size:0.8rem; color:var(--cf-muted); margin:4px 0 0; }

/* Mobile cards */
.cf-cards { padding:0.75rem; display:flex; flex-direction:column; gap:0.6rem; }
.cf-card { border:1px solid var(--cf-border); border-radius:var(--cf-radius-sm); overflow:hidden; background:#fff; }
.cf-card-head { display:flex; align-items:center; justify-content:space-between; padding:0.6rem 0.85rem; background:var(--cf-surface-alt); border-bottom:1px solid var(--cf-border); }
.cf-card-headmain { display:flex; align-items:center; gap:0.5rem; min-width:0; }
.cf-card-index { flex-shrink:0; display:inline-flex; align-items:center; justify-content:center; min-width:1.5rem; height:1.5rem; padding:0 5px; font-size:0.72rem; font-weight:700; color:#fff; background:rgb(var(--primary)); border-radius:999px; }
.cf-card-body { padding:0.75rem 0.85rem; }
.cf-card-grid { display:grid; grid-template-columns:1fr 1fr; gap:0.6rem; }
.cf-card-lbl { font-size:0.68rem; text-transform:uppercase; letter-spacing:0.03em; color:var(--cf-muted); margin:0 0 2px; }
.cf-card-val { font-size:0.82rem; font-weight:500; color:var(--cf-ink-strong); margin:0; }
.cf-card-val--muted { font-weight:400; color:var(--cf-muted); }
.cf-card-obs { margin-top:0.6rem; padding-top:0.6rem; border-top:1px solid var(--cf-surface-alt); }
.cf-card-foot { display:flex; align-items:center; justify-content:space-between; padding:0.5rem 0.85rem; border-top:1px solid var(--cf-border); background:var(--cf-surface); }
.cf-card-by { font-size:0.72rem; color:var(--cf-muted); }

/* Footer / pagination */
.cf-foot-info { font-size:0.78rem; color:var(--cf-muted); margin:0; }
.pagination { display:flex; align-items:center; gap:4px; }
.cf-ellipsis { padding:0 4px; color:var(--cf-faint); }
.page-btn { min-width:30px; height:30px; padding:0 8px; border:1px solid rgb(var(--input-border,203 213 225)); border-radius:var(--cf-radius-sm); background:#fff; color:var(--cf-ink); font-size:0.78rem; font-weight:600; cursor:pointer; transition:all 0.15s; }
.page-btn:hover:not(:disabled) { border-color:rgb(var(--primary) / 0.35); background:var(--cf-surface); }
.page-btn:disabled { opacity:0.5; cursor:not-allowed; }
.page-btn--active { background:rgb(var(--primary)); border-color:rgb(var(--primary)); color:#fff; }
.page-btn--nav { width:34px; display:inline-flex; align-items:center; justify-content:center; }

/* Transitions */
.modal-fade-enter-active, .modal-fade-leave-active { transition:opacity 0.25s ease; }
.modal-fade-enter-from, .modal-fade-leave-to { opacity:0; }
.modal-slide-enter-active, .modal-slide-leave-active { transition:all 0.25s ease; }
.modal-slide-enter-from, .modal-slide-leave-to { opacity:0; transform:translateY(-16px); }

/* Action sheet mobile (bloc conservé) */
.slide-up-enter-active, .slide-up-leave-active { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
.slide-up-enter-from, .slide-up-leave-to { opacity: 0; transform: translateY(100%); }
.safe-area-pb { padding-bottom: env(safe-area-inset-bottom, 0); }

.doc-scroll::-webkit-scrollbar { width:6px; height:6px; }
.doc-scroll::-webkit-scrollbar-thumb { background:#cbd5e1; border-radius:3px; }

@media (max-width:768px) { .cf-dialog--xxl { max-width:100%; } .doc-scroll { max-height:64vh; } }
</style>
